import React, { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { X, Minus, ExternalLink, Maximize2 } from "lucide-react";

/**
 * PopoutDialog — a dialog that can present its children as either a centered
 * MODAL (with a blocking overlay) or a detached, non-modal FLOATING panel that
 * leaves the page behind usable. Children stay mounted across the toggle, so
 * any typed form data is preserved. No browser popup (avoids popup blockers).
 *
 * Accessibility: role=dialog, aria-modal toggles with mode, Esc closes, first
 * focusable element is focused in modal mode.
 */
export function PopoutDialog({
  open,
  onOpenChange,
  title,
  subtitle,
  children,
  mode: initialMode = "modal", // "modal" | "float"
  allowFloat = true, // show the pop-out / dock toggle
  maxWidthClass = "max-w-2xl",
  testid = "popout-dialog",
  icon: Icon,
}) {
  const [mode, setMode] = useState(initialMode);
  const [minimized, setMinimized] = useState(false);
  const [pos, setPos] = useState(null); // custom {x,y} after drag (float mode)
  const panelRef = useRef(null);

  useEffect(() => {
    if (!open) {
      setMode(initialMode);
      setMinimized(false);
      setPos(null);
    }
  }, [open, initialMode]);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => { if (e.key === "Escape") onOpenChange(false); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, onOpenChange]);

  // Focus first field when shown as a modal (don't steal focus in float mode).
  useEffect(() => {
    if (!open || mode !== "modal") return undefined;
    const t = setTimeout(() => {
      const el = panelRef.current?.querySelector(
        "input,textarea,select,button:not([data-noautofocus]),[tabindex]"
      );
      if (el) el.focus();
    }, 60);
    return () => clearTimeout(t);
  }, [open, mode]);

  const onDragStart = useCallback((e) => {
    if (mode !== "float" || !panelRef.current) return;
    const pt = e.touches ? e.touches[0] : e;
    const rect = panelRef.current.getBoundingClientRect();
    const offX = pt.clientX - rect.left;
    const offY = pt.clientY - rect.top;
    const move = (ev) => {
      const p = ev.touches ? ev.touches[0] : ev;
      const x = Math.min(Math.max(4, p.clientX - offX), window.innerWidth - 80);
      const y = Math.min(Math.max(4, p.clientY - offY), window.innerHeight - 44);
      setPos({ x, y });
    };
    const up = () => {
      document.removeEventListener("mousemove", move);
      document.removeEventListener("mouseup", up);
      document.removeEventListener("touchmove", move);
      document.removeEventListener("touchend", up);
    };
    document.addEventListener("mousemove", move);
    document.addEventListener("mouseup", up);
    document.addEventListener("touchmove", move, { passive: false });
    document.addEventListener("touchend", up);
  }, [mode]);

  if (!open) return null;
  const isModal = mode === "modal";
  const floatStyle = pos
    ? { left: pos.x, top: pos.y, right: "auto", bottom: "auto" }
    : { right: 20, bottom: 20 };

  const panel = (
    <div
      ref={panelRef}
      role="dialog"
      aria-modal={isModal ? "true" : "false"}
      aria-label={title}
      data-testid={testid}
      data-mode={mode}
      className={
        isModal
          ? `fixed z-[71] left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[calc(100vw-1.5rem)] ${maxWidthClass} bg-white rounded-2xl shadow-2xl border border-[#E6EDF2] flex flex-col max-h-[90vh]`
          : `fixed z-[71] w-[min(94vw,460px)] bg-white rounded-2xl shadow-2xl border border-[#E6EDF2] flex flex-col ${minimized ? "" : "max-h-[80vh]"}`
      }
      style={isModal ? undefined : floatStyle}
    >
      <div
        onMouseDown={onDragStart}
        onTouchStart={onDragStart}
        className={`flex items-center gap-2 px-4 py-3 border-b border-[#E6EDF2] shrink-0 ${!isModal ? "cursor-move select-none" : ""}`}
      >
        {Icon ? <Icon size={16} className="text-[#1C4358] shrink-0" /> : null}
        <div className="min-w-0 flex-1">
          <div className="font-display font-bold text-[15px] text-[#252221] truncate">{title}</div>
          {subtitle ? <div className="text-[11.5px] text-[#6B7280] truncate">{subtitle}</div> : null}
        </div>
        <div className="flex items-center gap-1 shrink-0">
          {allowFloat && (isModal ? (
            <button type="button" data-noautofocus data-testid={`${testid}-popout`} title="Pop out" aria-label="Pop out to a floating window"
              onClick={() => setMode("float")} className="h-7 w-7 grid place-items-center rounded-md text-[#1C4358] hover:bg-[#1C4358]/10 transition-colors">
              <ExternalLink size={15} />
            </button>
          ) : (
            <button type="button" data-noautofocus data-testid={`${testid}-dock`} title="Dock to center" aria-label="Dock back to centered window"
              onClick={() => { setMode("modal"); setPos(null); }} className="h-7 w-7 grid place-items-center rounded-md text-[#1C4358] hover:bg-[#1C4358]/10 transition-colors">
              <Maximize2 size={15} />
            </button>
          ))}
          {!isModal && (
            <button type="button" data-noautofocus data-testid={`${testid}-minimize`} title={minimized ? "Expand" : "Minimize"} aria-label={minimized ? "Expand" : "Minimize"}
              onClick={() => setMinimized((m) => !m)} className="h-7 w-7 grid place-items-center rounded-md text-[#1C4358] hover:bg-[#1C4358]/10 transition-colors">
              <Minus size={15} />
            </button>
          )}
          <button type="button" data-noautofocus data-testid={`${testid}-close`} title="Close" aria-label="Close"
            onClick={() => onOpenChange(false)} className="h-7 w-7 grid place-items-center rounded-md text-[#1C4358] hover:bg-[#9C1E22]/10 hover:text-[#9C1E22] transition-colors">
            <X size={16} />
          </button>
        </div>
      </div>
      {!minimized && (
        <div className="overflow-y-auto mmm-scroll px-5 py-4 flex-1 min-h-0" data-testid={`${testid}-body`}>
          {children}
        </div>
      )}
    </div>
  );

  return createPortal(
    <>
      {isModal && (
        <div className="fixed inset-0 z-[70] bg-black/40 backdrop-blur-sm" data-testid={`${testid}-overlay`} onClick={() => onOpenChange(false)} />
      )}
      {panel}
    </>,
    document.body
  );
}

export default PopoutDialog;
